package io.jsonize.web.screens.editor

import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.viewinterop.WebElementView
import kotlinx.browser.document
import org.w3c.dom.HTMLDivElement
import kotlin.js.unsafeCast

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun CodeMirrorTextArea(
    value: String,
    onValueChange: (String) -> Unit,
    isEditable: Boolean,
    isDark: Boolean,
    languageExtension: Any? = null,
    placeholderText: String = "",
    modifier: Modifier = Modifier
) {
    val editorRef: MutableState<EditorView?> = remember { mutableStateOf(null) }
    val pushingFromCompose = remember { mutableStateOf(false) }
    val editableCompartment = remember { Compartment() }
    val languageCompartment = remember { Compartment() }
    val themeCompartment = remember { Compartment() }
    val onValueChangeState = rememberUpdatedState(onValueChange)

    WebElementView(
        factory = {
            val div = document.createElement("div") as HTMLDivElement
            div.style.width = "100%"
            div.style.height = "100%"
            div.style.background = "transparent"
            div.style.overflowX = "hidden"
            div.style.overflowY = "hidden"
            div.style.position = "relative"
            div
        },
        update = { el ->
            el.style.background = "transparent"
            if (editorRef.value == null) {
                val extensions = js("([])")
                extensions.push(lineNumbers())
                extensions.push(EditorView.lineWrapping)
                extensions.push(highlightActiveLineGutter())
                extensions.push(highlightActiveLine())
                extensions.push(indentOnInput())
                extensions.push(history())
                extensions.push(search())
                if (placeholderText.isNotBlank()) {
                    extensions.push(placeholder(placeholderText))
                }
                extensions.push(
                    themeCompartment.of(if (isDark) materialDark else materialLight)
                )

                val keymaps = js("([])")
                js("Array.prototype.push.apply")(keymaps, defaultKeymap)
                js("Array.prototype.push.apply")(keymaps, historyKeymap)
                js("Array.prototype.push.apply")(keymaps, searchKeymap)
                keymaps.push(indentWithTab)
                extensions.push(keymap.of(keymaps))

                extensions.push(
                    editableCompartment.of(EditorView.editable.of(isEditable))
                )
                extensions.push(
                    languageCompartment.of(languageExtension?.unsafeCast<dynamic>() ?: emptyExtension())
                )

                val listenerFn: (dynamic) -> Unit = { update ->
                    if (update.docChanged as Boolean) {
                        if (!pushingFromCompose.value) {
                            onValueChangeState.value(update.state.doc.toString())
                        }
                    }
                }
                extensions.push(EditorView.updateListener.of(listenerFn))

                val stateCfg = js("({})")
                stateCfg.doc = value
                stateCfg.extensions = extensions

                val state = EditorState.create(stateCfg)

                val viewCfg = js("({})")
                viewCfg.state = state
                viewCfg.parent = el

                val view = EditorView(viewCfg)
                view.asDynamic().dom.style.height = "100%"
                view.asDynamic().scrollDOM.style.height = "100%"
                view.asDynamic().scrollDOM.style.overflow = "auto"
                view.asDynamic().scrollDOM.style.overflowX = "hidden"
                editorRef.value = view
            }
        },
        onReset = {
            editorRef.value?.destroy()
            editorRef.value = null
        },
        modifier = modifier.fillMaxSize()
    )

    LaunchedEffect(value, editorRef.value) {
        val view = editorRef.value ?: return@LaunchedEffect
        val current = view.state.doc.toString()
        if (current != value) {
            pushingFromCompose.value = true
            view.dispatch(
                js(
                    "({ changes: { from: 0, to: view.state.doc.length, insert: value } })"
                )
            )
            pushingFromCompose.value = false
        }
    }

    LaunchedEffect(isEditable, editorRef.value) {
        val view = editorRef.value ?: return@LaunchedEffect
        val effect = editableCompartment.reconfigure(
            EditorView.editable.of(isEditable)
        )
        val tr = js("({})")
        tr.effects = arrayOf(effect)
        view.dispatch(tr)
    }

    LaunchedEffect(languageExtension, editorRef.value) {
        val view = editorRef.value ?: return@LaunchedEffect
        val effect = languageCompartment.reconfigure(
            languageExtension?.unsafeCast<dynamic>() ?: emptyExtension()
        )
        val tr = js("({})")
        tr.effects = arrayOf(effect)
        view.dispatch(tr)
    }

    LaunchedEffect(isDark, editorRef.value) {
        val view = editorRef.value ?: return@LaunchedEffect
        val effect = themeCompartment.reconfigure(
            if (isDark) materialDark else materialLight
        )
        val tr = js("({})")
        tr.effects = arrayOf(effect)
        view.dispatch(tr)
    }
}

private fun emptyExtension(): dynamic = js("([])")
